import { Flex, ThemeRoot, tokens, Typography, View } from '@sapphire-react/core';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { clampCoordinates } from './utils/clampCoordinates';

type Props = {
  label?: string;
  formattedValue?: string;
  breakdownRows?: {
    label: string;
    value: string;
  }[];
  resolvedColor?: string;
  mousePos: { x: number; y: number };
  isLeftSide?: boolean;
};

const CURSOR_OFFSET = Number.parseFloat(tokens.size.spacingSm.value);
const VIEWPORT_PADDING = Number.parseFloat(tokens.size.spacingXs.value);
const VERTICAL_OFFSET = Number.parseFloat(tokens.size.spacingLg.value);

export function DonutTooltip({
  label,
  formattedValue,
  breakdownRows,
  resolvedColor,
  mousePos,
  isLeftSide = false,
}: Props) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!tooltipRef.current) return;

    const rect = tooltipRef.current.getBoundingClientRect();
    setTooltipSize({ width: rect.width, height: rect.height });
  }, [label, formattedValue, resolvedColor, breakdownRows]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Re-measure on resize so clamping remains correct after orientation/viewport changes
    const handleResize = () => {
      if (!tooltipRef.current) return;

      const rect = tooltipRef.current.getBoundingClientRect();
      setTooltipSize({ width: rect.width, height: rect.height });
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const viewportWidth = typeof window === 'undefined' ? 0 : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? 0 : window.innerHeight;
  const cursorOffset = Number.isFinite(CURSOR_OFFSET) ? CURSOR_OFFSET : 0;
  const viewportPadding = Number.isFinite(VIEWPORT_PADDING)
    ? VIEWPORT_PADDING
    : 0;
  const verticalOffset = Number.isFinite(VERTICAL_OFFSET) ? VERTICAL_OFFSET : 0;

  // Preferred position keeps tooltip offset from cursor and biased left/right by segment side
  const desiredLeft = isLeftSide
    ? mousePos.x - cursorOffset - tooltipSize.width
    : mousePos.x + cursorOffset;
  const desiredTop = mousePos.y - verticalOffset;
  const maxLeft = Math.max(
    viewportPadding,
    viewportWidth - tooltipSize.width - viewportPadding,
  );
  const maxTop = Math.max(
    viewportPadding,
    viewportHeight - tooltipSize.height - viewportPadding,
  );

  // Final position is clamped so the tooltip stays fully visible on small screens
  const clampedLeft = clampCoordinates(desiredLeft, viewportPadding, maxLeft);
  const clampedTop = clampCoordinates(desiredTop, viewportPadding, maxTop);

  return createPortal(
    <ThemeRoot>
      <TooltipContainer
        ref={tooltipRef}
        style={{
          left: clampedLeft,
          top: clampedTop,
        }}
      >
        <Flex
          flexDirection={'column'}
          width={'100%'}
          gap={tokens.size.spacing4xs}
        >
          <Flex
            justifyContent={'space-between'}
            alignItems={'flex-start'}
            gap={tokens.size.spacing2xl}
          >
            <Flex alignItems={'flex-start'}>
              {resolvedColor && (
                <View
                  width={12}
                  height={12}
                  backgroundColor={resolvedColor}
                  marginRight={tokens.size.spacing2xs}
                  flexShrink={0}
                  marginTop={tokens.size.spacing3xs}
                />
              )}
              {label && <Typography.Body size={'sm'}>{label}</Typography.Body>}
            </Flex>
            {formattedValue && (
              <Typography.Body size={'sm'} UNSAFE_style={{ flexShrink: 0 }}>
                {formattedValue}
              </Typography.Body>
            )}
          </Flex>
          {breakdownRows?.map((row) => (
            <Flex
              key={row.label}
              justifyContent={'space-between'}
              alignItems={'center'}
              gap={tokens.size.spacingXl}
            >
              <Flex alignItems={'center'}>
                {resolvedColor && <MarkerSpacer aria-hidden />}
                <Typography.Body size={'sm'} color={'secondary'}>
                  {row.label}
                </Typography.Body>
              </Flex>
              <Typography.Body size={'sm'} color={'secondary'}>
                {row.value}
              </Typography.Body>
            </Flex>
          ))}
        </Flex>
      </TooltipContainer>
    </ThemeRoot>,
    document.body,
  );
}

const TooltipContainer = styled.div`
  position: fixed;
  pointer-events: none;
  border: 1px solid ${tokens.color.borderPrimary.value};
  background: ${tokens.color.backgroundPopover.value};
  border-radius: ${tokens.size.radiusMd.value};
  padding: ${tokens.size.spacingXs.value} ${tokens.size.spacingSm.value};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  z-index: 10;
  display: flex;
  align-items: flex-start;
  min-width: 180px;
  gap: ${tokens.size.spacing4xs.value};
`;

const MarkerSpacer = styled.div`
  width: ${tokens.size.spacingMd.value};
  height: ${tokens.size.spacingMd.value};
  flex-shrink: 0;
  visibility: hidden;
`;
